// Base transformer class with common utilities

import { IDataTransformer } from '../interfaces';

export abstract class BaseTransformer implements IDataTransformer {
  
  abstract transform(data: Record<string, unknown>): Promise<Record<string, unknown>>;

  // Remove undefined values to match schema strictly
  protected pruneUndefined<T extends Record<string, unknown>>(obj: T): T {
    const pruned: Record<string, unknown> = {};
    Object.keys(obj).forEach((k) => {
      const v = obj[k];
      if (v !== undefined) pruned[k] = v;
    });
    return pruned as T;
  }

  // Ensure a value is an array
  protected ensureArray(value: unknown): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value.map(String);
    if (typeof value === 'string') return [value];
    return [];
  }

  // Extract last name from owner field
  protected extractLastName(ownerText: string): string | null {
    if (!ownerText || ownerText.trim() === '') {
      return null;
    }

    const trimmed = ownerText.trim();
    if (trimmed.includes(' ')) {
      return trimmed.split(' ')[0]; // Take first word as last name
    } else {
      return trimmed; // If no space, entire string is the last name
    }
  }

  // Get current timestamp
  protected getCurrentTimestamp(): string {
    return new Date().toISOString();
  }

  // Parse integer safely
  protected safeParseInt(value: unknown, defaultValue: number = 0): number {
    return parseInt(String(value)) || defaultValue;
  }

  // Parse float safely
  protected safeParseFloat(value: unknown, defaultValue: number = 0): number {
    return parseFloat(String(value)) || defaultValue;
  }

  // Convert to boolean safely
  protected safeBoolean(value: unknown): boolean {
    return Boolean(value);
  }
} 